import { useState } from "react";
import { MapPin } from "lucide-react";
import { ATLAS_NODES, IMAGES } from "@/data/content";
import { Eyebrow } from "@/components/common";

const TYPES = ["All", ...Array.from(new Set(ATLAS_NODES.map((n) => n.type)))];

export default function Atlas() {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(ATLAS_NODES[0]);
  const shown = ATLAS_NODES.filter((n) => filter === "All" || n.type === filter);

  return (
    <div className="pt-16 lg:pt-[72px]" data-testid="atlas-page">
      <section className="py-14 lg:py-20 border-b border-indic-gold/15">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8">
          <Eyebrow>Interactive Civilisation Atlas</Eyebrow>
          <h1 className="mt-3 font-serif text-5xl lg:text-6xl font-semibold text-amber-50">The Atlas</h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-300/90 leading-relaxed">Universities, sacred cities, observatories, and ancient sites across the subcontinent. Select a marker to learn more.</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {TYPES.map((t) => (
              <button key={t} onClick={() => setFilter(t)} data-testid={`atlas-filter-${t.replace(/\s+/g, "-").toLowerCase()}`}
                className={`rounded-full px-4 py-1.5 text-sm transition-colors ${filter === t ? "bg-indic-gold text-indic-indigo" : "border border-indic-card-border text-slate-300 hover:border-indic-gold/40"}`}>
                {t}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 lg:py-14">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 relative rounded-sm border border-indic-card-border overflow-hidden bg-[#080c1a]" style={{ aspectRatio: "4 / 5", maxHeight: 640 }} data-testid="atlas-map">
            <img src={IMAGES.himalaya} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
            <svg className="absolute inset-0 h-full w-full opacity-[0.12]" preserveAspectRatio="none">
              {Array.from({ length: 12 }).map((_, i) => (
                <line key={`h${i}`} x1="0" y1={`${(i / 12) * 100}%`} x2="100%" y2={`${(i / 12) * 100}%`} stroke="#D4AF37" strokeWidth="0.5" />
              ))}
              {Array.from({ length: 10 }).map((_, i) => (
                <line key={`v${i}`} x1={`${(i / 10) * 100}%`} y1="0" x2={`${(i / 10) * 100}%`} y2="100%" stroke="#D4AF37" strokeWidth="0.5" />
              ))}
            </svg>
            {shown.map((n) => (
              <button key={n.name} onClick={() => setActive(n)} style={{ left: `${n.x}%`, top: `${n.y}%` }}
                className={`absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center group`} data-testid={`atlas-node-${n.name.toLowerCase()}`}>
                <span className={`relative flex h-3.5 w-3.5 ${active?.name === n.name ? "scale-150" : ""} transition-transform`}>
                  <span className={`absolute inline-flex h-full w-full rounded-full ${active?.name === n.name ? "bg-indic-gold animate-ping" : "bg-indic-terracotta/60"}`} />
                  <span className={`relative inline-flex h-3.5 w-3.5 rounded-full ${active?.name === n.name ? "bg-indic-gold" : "bg-indic-terracotta"} border border-[#0B1021]`} />
                </span>
                <span className={`mt-1 whitespace-nowrap text-[10px] font-sans tracking-wide ${active?.name === n.name ? "text-indic-gold" : "text-slate-300 opacity-0 group-hover:opacity-100"} transition-opacity`}>{n.name}</span>
              </button>
            ))}
          </div>

          <div className="rounded-sm border border-indic-card-border bg-indic-card p-7 self-start" data-testid="atlas-detail">
            {active && (
              <>
                <span className="inline-flex items-center gap-1.5 text-xs font-sans uppercase tracking-wider text-indic-gold"><MapPin size={13} /> {active.type}</span>
                <h2 className="mt-3 font-serif text-4xl text-amber-50">{active.name}</h2>
                <p className="mt-1 text-sm text-indic-sandstone">{active.epoch}</p>
                <div className="mt-4 h-px w-16 bg-indic-gold/40" />
                <p className="mt-4 text-slate-300/90 leading-relaxed">{active.note}</p>
              </>
            )}
            <div className="mt-8 space-y-1.5">
              {shown.map((n) => (
                <button key={n.name} onClick={() => setActive(n)} className={`block w-full text-left px-3 py-2 rounded-sm text-sm transition-colors ${active?.name === n.name ? "bg-indic-gold/15 text-indic-gold" : "text-slate-400 hover:text-amber-50 hover:bg-white/[0.03]"}`}>
                  {n.name} <span className="text-xs text-slate-600">· {n.type}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
